import { Link, styled } from "@mui/material";
import { Edit } from "@mui/icons-material";
import { Layer, GeoJSON } from "leaflet";
import { useTranslation } from "react-i18next";

import { ConfigParameters } from "types/types";
import { useSnackbar } from "notistack";
import { getFlandersStudyAreaIntersection } from "utils/DrawUtils";
import { area } from "@turf/turf";
import { getGeojsonFeature } from "utils/GeomanUtils";
import { latLonToLonLat } from "utils/TurfHelperUtils";
import { floor } from "lodash-es";

interface Props {
  layer: Layer | GeoJSON;
  configParameters: ConfigParameters;
  isEditing: boolean;
  isDisabled: boolean;
  addGeoJSONLayer: (geoJsonLayer: Layer | GeoJSON) => void;
  setIsEditing: (isEditing: boolean) => void;
}

export function EditStudyArea(props: Props) {
  const {
    layer,
    configParameters,
    isEditing,
    isDisabled,
    setIsEditing,
    addGeoJSONLayer,
  } = props;
  const { t } = useTranslation();
  const prevLayerShape = getGeojsonFeature(layer);
  const { enqueueSnackbar } = useSnackbar();

  function onEdit() {
    setIsEditing(true);
    //@ts-ignore
    layer.pm.toggleEdit({
      limitMarkersToCount: 25,
    });
    layer.on("pm:markerdragend", (e) => {
      // e.markerEvent.target.closeTooltip();
      e.markerEvent.target.unbindTooltip();
    });

    layer.on("pm:markerdragstart", (e) => {
      e.markerEvent.target.bindTooltip(t("label.areaSqM", { area: 0 }), {
        permanent: true,
      });
    });

    layer.on("pm:markerdrag	", (e) => {
      // @ts-ignore
      const tooltip = e.markerEvent.target.getTooltip();
      if (tooltip) {
        const polygon = getGeojsonFeature(e.layer);
        const sqM = floor(area(polygon), 2);
        tooltip.setContent(
          t("label.areaSqM", { area: sqM.toLocaleString("nl-BE") })
        );
      }
    });
    layer.on("pm:update	", ({ layer }) => {
      const { studyAreaMin_m2, studyAreaMax_m2, domain_4326 } =
        configParameters;
      const intersection = getFlandersStudyAreaIntersection(domain_4326, layer);
      if (!intersection) return;

      const surfaceArea = area(intersection);
      if (surfaceArea < studyAreaMin_m2 || surfaceArea > studyAreaMax_m2) {
        enqueueSnackbar(
          t("label.studyAreaShouldBeBetween", {
            min: studyAreaMin_m2,
            max: studyAreaMax_m2.toLocaleString("nl-BE"),
          }),
          {
            variant: "error",
          }
        );
        layer.setLatLngs(
          //@ts-ignore
          latLonToLonLat(prevLayerShape.geometry.coordinates)
        );
        return cleanUp();
      }
      //@ts-ignore
      layer.setLatLngs(
        //@ts-ignore
        latLonToLonLat(intersection.geometry.coordinates)
      );
      addGeoJSONLayer(layer);
      return cleanUp();
    });
  }

  function onSave() {
    setIsEditing(false);
    //@ts-ignore
    layer.pm.toggleEdit({ limitMarkersToCount: 25 });
  }

  function cleanUp() {
    layer.off("pm:update");
    layer.off("pm:markerdrag");
    layer.off("pm:markerdragstart");
    layer.off("pm:markerdragend");
  }

  return (
    <StyledDiv>
      {isEditing && !isDisabled && (
        <>
          <Edit color={"success"} />
          <Link className="success" onClick={onSave}>
            {t("label.save")}
          </Link>
        </>
      )}
      {!isEditing && !isDisabled && (
        <>
          <Edit color={"primary"} />
          <Link onClick={onEdit}>{t("label.editPolygon")}</Link>
        </>
      )}
      {isDisabled && (
        <>
          <Edit color={"disabled"} />
          <Link onClick={onEdit} className="disabled">
            {t("label.editPolygon")}
          </Link>
        </>
      )}
    </StyledDiv>
  );
}

const StyledDiv = styled("div")`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  .MuiLink-root {
    &.success {
      color: ${({ theme }) => theme.palette.success.main};
    }
    &.disabled {
      pointer-events: none;
      text-decoration: none;
      cursor: not-allowed;
      color: ${({ theme }) => theme.palette.grey[400]};
    }
  }
`;
